import type { SupportedLanguage } from "./constants";

export type ResourceData = Record<string, unknown>;

export interface I18nBundle {
  ns: string;
  resources: Partial<Record<SupportedLanguage, ResourceData>>;
}

type BundleListener = (bundle: I18nBundle) => void;

const bundles = new Map<string, I18nBundle>();
const listeners = new Set<BundleListener>();

function isRecord(value: unknown): value is ResourceData {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMerge(base: ResourceData, patch: ResourceData): ResourceData {
  const out: ResourceData = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const existing = out[key];
    out[key] =
      isRecord(existing) && isRecord(value)
        ? deepMerge(existing, value)
        : value;
  }
  return out;
}

/**
 * Register a namespace's resources. Registering an existing namespace
 * deep-merges into it. Listeners (the client singleton, cached server
 * instances) are notified so late registration takes effect immediately.
 */
export function registerBundle(bundle: I18nBundle): void {
  const existing = bundles.get(bundle.ns);
  if (existing) {
    const merged: I18nBundle = {
      ns: bundle.ns,
      resources: { ...existing.resources },
    };
    for (const [lng, data] of Object.entries(bundle.resources) as [
      SupportedLanguage,
      ResourceData | undefined,
    ][]) {
      if (!data) continue;
      merged.resources[lng] = deepMerge(existing.resources[lng] ?? {}, data);
    }
    bundles.set(bundle.ns, merged);
  } else {
    bundles.set(bundle.ns, bundle);
  }
  for (const listener of listeners) listener(bundle);
}

export function subscribeBundles(listener: BundleListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Resources in i18next's `{ [lng]: { [ns]: data } }` shape. */
export function getRegisteredResources(): Partial<
  Record<SupportedLanguage, Record<string, ResourceData>>
> {
  const out: Partial<Record<SupportedLanguage, Record<string, ResourceData>>> =
    {};
  for (const bundle of bundles.values()) {
    for (const [lng, data] of Object.entries(bundle.resources) as [
      SupportedLanguage,
      ResourceData | undefined,
    ][]) {
      if (!data) continue;
      (out[lng] ??= {})[bundle.ns] = data;
    }
  }
  return out;
}
